import {
  TCategoryTypeValueInfo,
  TFilterSortContextInfo,
  TSDMSelectedFiltersType,
  TSDMSortOptionsType,
} from "@ldp/common-types";
import { useRedirectLink } from "../utils";
import {
  TSDMFilterGroup,
  TSDMFilterModel,
  useFilterTracking,
} from "@ldp/sdm-analytics-utils";
import { useUpdateFilterQueryParams } from "../hooks";
import { Option } from "@ldp-web/component-library/types";

export enum ListingURLParamType {
  /** The param type for sort */
  SORT = "sort",
  /** The param type for pagination */
  PAGE = "page",
  /** The param type for search */
  TEXT = "text",
}

/** The schema for the listing data to be added to the request */
export type TListingInfo = {
  /** The filters to be applied to the listing */
  filters?: {
    [key: string]: string[];
  };
  /** The sort to be applied to the listing */
  sort?: {
    [key: string]: string;
  };
  /** The pagination to be applied to the listing */
  pagination?: {
    from: number;
    size?: number;
  };
  /** Whether to include filters in the response */
  includeFiltersInResponse: boolean;
};

/**
 * Function to parse the URL and return the listing info object
 * @param href The URL to parse
 * @returns The listing info object
 */
export const getListingInfoFromURL = (search: URL["search"]): TListingInfo => {
  const pagination: TListingInfo["pagination"] = {
    from: 1,
  };
  const sort: TListingInfo["sort"] = {};
  const filters: TListingInfo["filters"] = {};
  const exisitingParams = new URLSearchParams(search);
  // Loop through all the params and add them to sort, pagination or filters
  for (const [key, value] of exisitingParams.entries()) {
    if (key === ListingURLParamType.SORT) {
      // Split the sort value and add to sort object as key and value
      const sortValue = value.split("-");
      const sortSelected = sortValue[1] ? sortValue[1] : "";
      sort[sortValue[0]] = sortSelected;
    } else if (key === ListingURLParamType.PAGE) {
      // Add pagination if page exists
      pagination.from = Number(value);
    } else {
      const decodedValue = decodeURIComponent(value);
      // All other params are filters
      filters[key]
        ? filters[key].push(decodedValue)
        : (filters[key] = [decodedValue]);
    }
  }

  return {
    filters,
    sort,
    pagination,
    includeFiltersInResponse: true,
  };
};

/**
 * Function to handle filter click and also remove individual filters
 * @param val selected filter value
 * appends the selected filter in the query paramter, makes api call
 * with selected fitlers
 *
 */
export const handleFilterSelect = (
  val: TSDMSelectedFiltersType,
  filterData: TSDMFilterGroup[] | null,
  updateQueryParamsCB: ReturnType<typeof useUpdateFilterQueryParams>,
  trackFilterFn: ReturnType<typeof useFilterTracking>,
  redirectToPage: ReturnType<typeof useRedirectLink>
) => {
  const paramsKey = Object.keys(val)[0];
  const selectedCode = val[paramsKey].code;
  const queryParam = {
    [paramsKey]: encodeURIComponent(selectedCode),
    [ListingURLParamType.PAGE]: "1",
  };
  const isFilterSelected = val[paramsKey].selected;
  trackSelectedFilters(
    paramsKey,
    selectedCode,
    isFilterSelected,
    filterData,
    trackFilterFn
  );
  const updatedUrl = updateQueryParamsCB(queryParam, isFilterSelected);
  redirectToPage(updatedUrl);
};

/**
 * Function to make filter track call with selected filters
 * @param selectedGroupCode selected filter group code
 * @param selectedCode selected filter code
 * @param isSelected boolean to check if it's selected or deselected
 * updates filter groups with selected data and makes analytics call
 * with selected filters
 */
export const trackSelectedFilters = (
  selectedGroupCode: string,
  selectedCode: string,
  isSelected: boolean,
  filterData: TSDMFilterGroup[] | null,
  trackFilterFn: ReturnType<typeof useFilterTracking>
) => {
  const updatedFilterGroups = filterData?.map((filterGroup) => {
    if (filterGroup.code === selectedGroupCode) {
      filterGroup.values.forEach((filter) => {
        if (filter.code === selectedCode) {
          filter.selected = isSelected;
        }
      });
    }
    return filterGroup;
  });

  if (updatedFilterGroups) {
    /** track event call for filters */
    trackFilterFn({
      filterGroups: updatedFilterGroups as TSDMFilterGroup[],
    });
  }
};

/**
 * Function to handle sort click
 * @param val selected value of sort
 * updates query parameter and selected sort value, makes api call
 */
export const handleSortSelect = (val: string, sorts: Option[] | null) => {
  const sortValue = sorts
    ? sorts
        .filter((sort) => sort.name === val)
        .map((selectedVal) => {
          return selectedVal.code;
        })
    : [""];
  const queryParam = {
    [ListingURLParamType.SORT]: sortValue[0],
    [ListingURLParamType.PAGE]: "1",
  };

  return queryParam;
};

/**
 * Function to parse the filtersSection and sortsSection data and return the selected items as context
 * @param filterGroups filtersSection from the api response
 * @param sorts sortsSection from the api response
 * @returns Object containing the selected filters and sort
 */
export const getFilterAndSortContext = (
  filterGroups: TSDMFilterGroup[],
  sorts: TSDMSortOptionsType[]
): TFilterSortContextInfo => {
  const filters: TCategoryTypeValueInfo[] = [];

  // check the selected sort
  const sort: string = sorts?.find((item) => item.selected)?.name || "";

  // check the selected filters and create a list of key and label
  filterGroups?.forEach((filterGroup: TSDMFilterGroup) => {
    filterGroup.values?.forEach(
      (subFilter: TSDMFilterModel) =>
        subFilter.selected &&
        filters.push({ category: filterGroup.code, value: subFilter.name })
    );
  });

  return {
    filters,
    sort,
  };
};
